import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { usePatientNotes } from '../../../../hooks/usePatientNotes';
import { SEVERITY } from '../../notes/severity';
import { SeverityPill } from '../../notes/SeverityPill';
import { VisibilityPill } from '../../notes/VisibilityPill';
import { NoteBody } from '../../notes/NoteBody';
import { Card } from '../shared';

interface PatientNotesCardProps {
    patientId: string;
    onViewAll?: () => void;
}

function PatientNotesCard({ patientId, onViewAll }: PatientNotesCardProps) {
    const { notes, isLoading } = usePatientNotes(patientId);

    const criticalCount = notes.filter((n) => n.severity === 'critical').length;
    const warningCount = notes.filter((n) => n.severity === 'warning').length;
    const infoCount = notes.filter((n) => n.severity === 'info').length;

    // Backend returns notes in canonical order; filter only, no re-sort needed.
    const urgent = notes.filter((n) => n.severity === 'critical' || n.severity === 'warning');

    const headerRight =
        notes.length > 0 ? (
            <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5">
                    {criticalCount > 0 && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold font-inter bg-red-100 text-red-700">
                            <span className="w-1 h-1 rounded-full bg-current opacity-70" />
                            {criticalCount} critical
                        </span>
                    )}
                    {warningCount > 0 && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold font-inter bg-amber-100 text-amber-700">
                            <span className="w-1 h-1 rounded-full bg-current opacity-70" />
                            {warningCount} warning
                        </span>
                    )}
                    {infoCount > 0 && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold font-inter bg-blue-100 text-blue-700">
                            <span className="w-1 h-1 rounded-full bg-current opacity-70" />
                            {infoCount} info
                        </span>
                    )}
                </div>
                <button
                    onClick={onViewAll}
                    className="text-[11px] font-semibold font-inter text-gray-500 hover:text-gray-800 transition-colors"
                >
                    View all →
                </button>
            </div>
        ) : undefined;

    if (isLoading) {
        return (
            <Card title="Patient notes">
                <div className="h-20 rounded-lg bg-gray-100 animate-pulse" />
            </Card>
        );
    }

    return (
        <Card title="Patient notes" headerRight={headerRight}>
            {urgent.length === 0 ? (
                <div className="flex items-center gap-2.5 py-2 text-gray-400">
                    <InformationCircleIcon className="w-4 h-4 opacity-40 flex-shrink-0" />
                    <span className="text-[12px] font-inter">No critical or warning notes</span>
                </div>
            ) : (
                <div className="flex flex-col gap-2">
                    {urgent.slice(0, 3).map((note) => {
                        const s = SEVERITY[note.severity];
                        const Icon = s.icon;
                        return (
                            <div
                                key={note.id}
                                className={`flex items-start gap-2.5 rounded-lg border ${s.border} ${s.bg} px-3 py-2.5`}
                            >
                                <span className={`flex-shrink-0 mt-0.5 ${s.iconColor}`}>
                                    <Icon className="w-3.5 h-3.5" />
                                </span>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="text-[12px] font-bold text-gray-900 font-inter">
                                            {note.title}
                                        </span>
                                        <SeverityPill severity={note.severity} size="xs" />
                                        <VisibilityPill visibility={note.visibility} size="xs" />
                                    </div>
                                    <div className="mt-0.5">
                                        <NoteBody note={note} compact />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    {urgent.length > 3 && (
                        <button
                            onClick={onViewAll}
                            className="text-[11px] font-semibold font-inter text-gray-500 hover:text-gray-800 text-left"
                        >
                            +{urgent.length - 3} more notes → view all
                        </button>
                    )}
                </div>
            )}
        </Card>
    );
}

export { PatientNotesCard };
